import {
  handleAssignDeviceToStudy,
  handleSendStudyReferenceCode,
  STUDY_STATUS,
  STUDY_PENDING_COMMAND,
  checkHasSentRefCode,
  getSendRefCodeSuccessfullyMessage,
  getSendRefCodeUnsuccessfullyMessage,
} from '@/entities/study';
import { formatPhysicianOptionLabel } from '@/entities/physician';
import { FLAGGED_REASON } from '@/shared/config';
import {
  isWebViewCreateStudy,
  auth,
  generateFullName,
  showMessageError,
  showMessageSuccess,
} from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { t } from 'ttag';
import type {
  ClickedData,
  EditDrawerState,
  PhysicianData,
  PhysicianOption,
  StudyDetailsTabProps,
  StudyDetailsTabState,
} from './studyDetailsTab.types';
import type { CallBackUpdateData } from './editStudyDetails/editStudyDetails.types';

/**
 * Supplies the study details tab with its modal and drawer state, derived study flags and action
 * handlers. Derives edit permission, end-of-duration, pending-command and mark-addressed flags and
 * physician select options from the study, closes the live ECG modal once the study is completed,
 * done or aborted, and wires the reference-code resend, live ECG, abort, assign-device and
 * edit-drawer handlers that call the parent callbacks.
 *
 * @param props - The study data, referring and interpreting physician lists, abort flags and
 * handleAbortStudy, and the updateSentReferenceCodeStatus, handleUpdateStudyAfterAssigningDevice and
 * updateStudyData callbacks.
 * @returns An object carrying the tab state and setter, the edit-drawer state and setter, the
 * web-view and live ECG modal refs, the derived flags, the physician options and every modal, drawer,
 * live ECG, abort, assign and update handler.
 */
export const useStudyDetailsTab = ({
  data,
  referringPhysicians,
  interprettingPhysicians,
  isAbortedStudy,
  isAbortingStudy,
  handleAbortStudy,
  updateSentReferenceCodeStatus,
  handleUpdateStudyAfterAssigningDevice,
  updateStudyData: updateStudyDataProp,
}: Pick<
  StudyDetailsTabProps,
  | 'data'
  | 'referringPhysicians'
  | 'interprettingPhysicians'
  | 'isAbortedStudy'
  | 'isAbortingStudy'
  | 'handleAbortStudy'
  | 'updateSentReferenceCodeStatus'
  | 'handleUpdateStudyAfterAssigningDevice'
  | 'updateStudyData'
>) => {
  const isWebView = useRef(isWebViewCreateStudy());
  const viewLiveEcgModalRef = useRef<{ resetPause: () => void } | null>(null);

  const [state, setState] = useSetState<StudyDetailsTabState>({
    isOpenResendModal: false,
    isOpenTooManyRequestModal: false,
    isOpenSolutionsModal: false,
    isVisibleDeviceInfoDrawer: false,
    openLiveEcgModal: false,
    openConfirmActivityModal: false,
    openStopLiveEcgModal: false,
    openConfirmAbortStudy: false,
    isCallingAbortStudy: false,
    visibleAssignDeviceDrawer: false,
    displayedSolutionStudy: null,
    displayedSolutionData: null as any,
    tooManyRequestMessage: '',
    isSaving: false,
    clickedData: undefined,
  });

  /**
   * Memoized permission flag for editing the study amendment note. True when the authenticated user id equals the id of the study interpreting physician.
   *
   * @returns Whether the current user may edit the amendment note.
   */
  const isPermitEditAmendmentNote = useMemo(
    () => auth.userId() === data?.info?.interprettingPhysician?.id,
    [auth, data?.info?.interprettingPhysician?.id],
  );

  /**
   * Memoized flag that disables editing of the study details. Resolves to true when the study is aborted or currently being aborted.
   *
   * @returns Whether editing is disabled.
   */
  const isDisableEdit = useMemo(
    () => isAbortedStudy || isAbortingStudy,
    [isAbortedStudy, isAbortingStudy],
  );

  const [editDrawer, setEditDrawer] = useSetState<EditDrawerState>({});

  /**
   * Closes one of the study details edit drawers. Sets that drawer's flag to false in the editDrawer state keyed by name.
   *
   * @param key - Key identifying the drawer to close.
   * @returns Nothing.
   */
  const onCloseDrawer = (key: string) => {
    setEditDrawer({ [key]: false });
  };

  /**
   * Opens one of the study details edit drawers. Sets that drawer's flag to true in the editDrawer state keyed by name.
   *
   * @param key - Key identifying the drawer to open.
   * @returns Nothing.
   */
  const showStudyInfoDrawer = (key: string) => {
    setEditDrawer({ [key]: true });
  };

  /**
   * Memoized flag telling whether the study monitoring window has already elapsed. Compares the current time against the study stop timestamp with dayjs, recomputed whenever the study data changes.
   *
   * @returns Whether now is after the study stop time.
   */
  const isEndOfDuration = React.useMemo(() => dayjs().isAfter(dayjs(data?.stop)), [data]);

  // *: Started study has sent reference code automatically
  const hasSentRefCode = checkHasSentRefCode((data ?? {}) as any);

  /**
   * Derived flag telling whether the study still has an unapplied UPDATE_STUDY pending command. True only when the study status is neither COMPLETED nor DONE, the study is not a test study, its pendingCommand equals STUDY_PENDING_COMMAND.UPDATE_STUDY, and the monitoring duration has not ended.
   *
   * @returns Whether an update-study command is still pending.
   */
  const hasPendingCommandUpdateStudy =
    !_.includes([STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE], data?.status) &&
    !data?.isTest &&
    data?.pendingCommand?.command === STUDY_PENDING_COMMAND.UPDATE_STUDY &&
    !isEndOfDuration;

  const isStopping = data?.isStopping;

  /**
   * Memoized flag that hides the mark-as-addressed action for a flagged study. For a study flagged with reason PENDING_UPLOAD it is true only when the status is DONE; otherwise it is true when the status is COMPLETED or DONE, or the monitoring duration has ended.
   *
   * @returns Whether the mark-addressed action is hidden.
   */
  const isHideMarkAddressedStudy = useMemo(() => {
    if (data?.flaggedStudy?.reason === FLAGGED_REASON.PENDING_UPLOAD) {
      return data?.status === STUDY_STATUS.DONE;
    }
    return _.includes([STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE], data?.status) || isEndOfDuration;
  }, [data?.status, isEndOfDuration]);

  /**
   * Flips the solutions modal for the study being viewed. Assembles a study summary of artifact, flagged study, device and the patient's full name, gender, date of birth and contact, stores it as the displayed solution study and inverts isOpenSolutionsModal.
   *
   * @returns Nothing.
   */
  const toggleDisplaySolutionsDrawer = () => {
    const study = {
      artifact: data?.artifact,
      patient: {
        name: generateFullName(data?.info?.patient),
        gender: data?.info?.patient?.gender,
        dob: data?.info?.patient?.dob,
        contact: data?.info?.patient?.contact,
      },
      flaggedStudy: data?.flaggedStudy,
      device: data?.device,
    };
    setState({
      isOpenSolutionsModal: !state.isOpenSolutionsModal,
      displayedSolutionStudy: study,
    });
  };

  /**
   * Resends the study's reference code to the patient. Picks the success and failure messages from whether a code was already sent, submits the request, and on success toasts, closes the resend modal and stamps sentReferenceCodeAt on the study; a rate-limit message opens the too-many-requests modal instead, other outcomes and thrown errors toast the failure, and the saving flag is cleared either way.
   *
   * @returns A promise resolving once the resend attempt has finished.
   */
  const resendReferenceCode = async () => {
    const successfulMessage = getSendRefCodeSuccessfullyMessage(hasSentRefCode);
    const failedMessage = getSendRefCodeUnsuccessfullyMessage(hasSentRefCode);

    try {
      setState({ isSaving: true });

      // For customer environment
      const { id } = data || {};
      const result = await handleSendStudyReferenceCode({ studyId: id });
      // *: For testing (CP-2.25.1)
      // const result = {
      //   isSuccess: true,
      // };

      if (result?.isSuccess) {
        showMessageSuccess({ content: successfulMessage });
        setState({ isOpenResendModal: false });
        updateSentReferenceCodeStatus({
          ...data,
          sentReferenceCodeAt: dayjs().toISOString(),
        });
      } else if (_.includes(result?.message, 'before resending the reference code.')) {
        setState({
          isOpenResendModal: false,
          isOpenTooManyRequestModal: true,
          tooManyRequestMessage: result?.message ?? undefined,
        });
      } else {
        showMessageError({ content: failedMessage });
        setState({ isOpenResendModal: false });
      }
      setState({ isSaving: false });
    } catch {
      showMessageError({ content: failedMessage });
      setState({ isSaving: false });
    }
  };

  /**
   * Flips the device info drawer of the study details tab. Stores the clicked row data and inverts isVisibleDeviceInfoDrawer.
   *
   * @param clickedData - Data of the clicked device row, omitted when closing.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedData?: ClickedData) => {
    setState({
      clickedData,
      isVisibleDeviceInfoDrawer: !state.isVisibleDeviceInfoDrawer,
    });
  };

  /**
   * Shows or hides the live ECG activity confirmation modal. Writes the flag to openConfirmActivityModal in state.
   *
   * @param openConfirmActivityModal - True to show the activity confirmation modal.
   * @returns Nothing.
   */
  const displayConfirmActivityModal = (openConfirmActivityModal: boolean) => {
    setState({ openConfirmActivityModal });
  };

  /**
   * Shows or hides the stop live ECG modal. Writes the flag to openStopLiveEcgModal in state.
   *
   * @param openStopLiveEcgModal - True to show the stop live ECG modal.
   * @returns Nothing.
   */
  const displayStopLiveEcgModal = (openStopLiveEcgModal: boolean) => {
    setState({ openStopLiveEcgModal });
  };

  /**
   * Shows or hides the live ECG viewer modal. Writes the flag to openLiveEcgModal in state.
   *
   * @param openLiveEcgModal - True to show the live ECG viewer.
   * @returns Nothing.
   */
  const displayViewLiveEcg = (openLiveEcgModal: boolean) => {
    setState({ openLiveEcgModal });
  };

  /**
   * Confirms that the user is still watching the live ECG. Calls resetPause on the live ECG modal ref and closes the activity confirmation modal.
   *
   * @returns Nothing.
   */
  const onClickConfirmActivityModal = () => {
    viewLiveEcgModalRef.current?.resetPause();
    displayConfirmActivityModal(false);
  };

  /**
   * Stops the live ECG stream. Closes the activity confirmation and the live ECG viewer, then opens the stop live ECG modal.
   *
   * @returns Nothing.
   */
  const handleStopStreaming = () => {
    displayConfirmActivityModal(false);
    displayViewLiveEcg(false);
    displayStopLiveEcgModal(true);
  };

  /**
   * Resumes live ECG streaming after it was stopped. Hides the stop live ECG modal and reopens the live ECG viewer.
   *
   * @returns Nothing.
   */
  const handleResumeStreaming = () => {
    displayStopLiveEcgModal(false);
    displayViewLiveEcg(true);
  };

  /**
   * Asks the user to confirm aborting the study. Sets openConfirmAbortStudy to true.
   *
   * @returns Nothing.
   */
  const handleAbortClick = () => {
    setState({ openConfirmAbortStudy: true });
  };

  /**
   * Dismisses the abort-study confirmation. Sets openConfirmAbortStudy to false.
   *
   * @returns Nothing.
   */
  const handleCancelClick = () => {
    setState({ openConfirmAbortStudy: false });
  };

  /**
   * Settles the abort-study confirmation once the parent abort request finishes. Clears
   * isCallingAbortStudy and, when the abort succeeded, also closes the confirmation.
   *
   * @param isSuccess - Whether the study was aborted.
   * @returns Nothing.
   */
  const callbackAbortStudy = (isSuccess: boolean) => {
    if (isSuccess) {
      setState({
        openConfirmAbortStudy: false,
        isCallingAbortStudy: false,
      });
    } else {
      setState({ isCallingAbortStudy: false });
    }
  };

  /**
   * Aborts the study from the drawer's primary button. Raises the isCallingAbortStudy flag and calls handleAbortStudy with the completion callback.
   *
   * @returns A promise resolving once the abort request has been dispatched.
   */
  const handleRightButtonClick = async () => {
    setState({ isCallingAbortStudy: true });
    handleAbortStudy(callbackAbortStudy);
  };

  /**
   * Opens the assign-device drawer for the study. Sets visibleAssignDeviceDrawer to true.
   *
   * @returns Nothing.
   */
  const handleAssignDeviceClick = () => {
    setState({ visibleAssignDeviceDrawer: true });
  };

  /**
   * Assigns the selected device to the study. Awaits the assignment with the study ID, device ID, device type and study setting, applies the returned study locally, closes the assign-device drawer and toasts that the device can now be found in the Active tab; a failure logs and toasts the error message, and the callback always runs.
   *
   * @param studyId - Study receiving the device.
   * @param deviceId - Device being assigned.
   * @param deviceType - Model of the device being assigned.
   * @param studySetting - Recording and detection settings applied with the assignment.
   * @param callbackAssignDevice - Callback run once the attempt finishes, whether it succeeded or not.
   * @returns A promise resolving once the assignment attempt has finished.
   */
  const handleAssignDeviceDrawerClick = async (
    {
      studyId,
      deviceId,
      deviceType,
      studySetting,
    }: {
      studyId: string;
      deviceId: string;
      deviceType: string;
      studySetting: {
        duration: number;
        preEventTime: number;
        postEventTime: number;
        bradycardiaThreshold: number;
        tachycardiaThreshold: number;
        pauseLevel: number;
        pauseDetection: boolean;
        afibDetection: boolean;
        diagnosticLead: number;
        algAmp: number;
        ringId: string;
      };
    },
    callbackAssignDevice: () => void,
  ) => {
    try {
      const assignedDeviceStudy = await handleAssignDeviceToStudy({
        studyId,
        deviceId,
        deviceType,
        studySetting,
      });
      if (assignedDeviceStudy) {
        const assignedStudySetting = assignedDeviceStudy.info?.studySetting;
        handleUpdateStudyAfterAssigningDevice({
          id: assignedDeviceStudy.id,
          isDeviceAssignmentPending: assignedDeviceStudy.isDeviceAssignmentPending ?? false,
          device: {
            deviceId: assignedDeviceStudy.device?.deviceId ?? '',
            model: assignedDeviceStudy.device?.model ?? '',
          },
          info: {
            studySetting: {
              duration: assignedStudySetting?.duration ?? 0,
              preEventTime: assignedStudySetting?.preEventTime ?? 0,
              postEventTime: assignedStudySetting?.postEventTime ?? 0,
              bradycardiaThreshold: assignedStudySetting?.bradycardiaThreshold ?? 0,
              tachycardiaThreshold: assignedStudySetting?.tachycardiaThreshold ?? 0,
              pauseLevel: assignedStudySetting?.pauseLevel ?? 0,
              pauseDetection: assignedStudySetting?.pauseDetection ?? false,
              afibDetection: assignedStudySetting?.afibDetection ?? false,
              diagnosticLead: assignedStudySetting?.diagnosticLead ?? 0,
              algAmp: assignedStudySetting?.algAmp ?? 0,
              ringId: assignedStudySetting?.ringId ?? '',
            },
          },
        });
      }
      setState({ visibleAssignDeviceDrawer: false });
      showMessageSuccess({
        content: t`Device assigned successfully. You can now find it in the Active tab.`,
      });
    } catch (error) {
      console.error('Failed to assign device to study: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    } finally {
      callbackAssignDevice();
    }
  };

  /**
   * Commits an edit made in one of the study details drawers. Closes the drawer identified by the key, then forwards the changed study data and the report-size check flag to the parent update callback.
   *
   * @param data - Changed study fields to merge into the study detail.
   * @param key - Key of the drawer that produced the change.
   * @param couldCheckReportSize - Whether the parent should re-check the report size.
   * @returns Nothing.
   */
  const updateStudyData = (
    data: CallBackUpdateData,
    key: string,
    couldCheckReportSize?: boolean,
  ) => {
    onCloseDrawer(key);
    updateStudyDataProp(data, couldCheckReportSize);
  };

  useEffect(() => {
    const setStateObject = {};
    const notDisplayedViewLiveEcgStudyStatus = [
      STUDY_STATUS.COMPLETED,
      STUDY_STATUS.DONE,
      STUDY_STATUS.ABORTED,
    ];
    if (notDisplayedViewLiveEcgStudyStatus.includes(data?.status ?? '')) {
      Object.assign(setStateObject, { openLiveEcgModal: false });
    }
    setState(setStateObject);
  }, [data]);

  /**
   * Memoized select options for the referring physician field of the study details form. Maps each referring physician to an option carrying the formatted label, the physician id as value, the full name as title and name, plus phone1, nightPhone, fax and email contact details.
   *
   * @returns Options built from the referring physician list.
   */
  const referringPhysicianOptions = useMemo(() => {
    const options: PhysicianOption[] = referringPhysicians.map((physician: PhysicianData) => ({
      label: formatPhysicianOptionLabel(physician),
      value: physician.id,
      title: generateFullName(physician),
      name: generateFullName(physician),
      phone1: physician.contact?.phone1,
      nightPhone: physician.contact?.nightPhone,
      fax: physician.contact?.fax,
      email: physician.email,
    }));
    return options;
  }, [referringPhysicians]);

  /**
   * Memoized select options for the interpreting physician field of the study details form. Maps each interpreting physician to an option carrying the formatted label, the physician id as value, the full name as title and name, plus phone1, nightPhone, fax and email contact details.
   *
   * @returns Options built from the interpreting physician list.
   */
  const interprettingPhysicianOptions = useMemo(() => {
    const options: PhysicianOption[] = interprettingPhysicians.map((physician: PhysicianData) => ({
      label: formatPhysicianOptionLabel(physician),
      value: physician.id,
      title: generateFullName(physician),
      name: generateFullName(physician),
      phone1: physician.contact?.phone1,
      nightPhone: physician.contact?.nightPhone,
      fax: physician.contact?.fax,
      email: physician.email,
    }));
    return options;
  }, [interprettingPhysicians]);

  return {
    state,
    setState,
    editDrawer,
    setEditDrawer,
    isWebView,
    viewLiveEcgModalRef,
    isPermitEditAmendmentNote,
    isDisableEdit,
    isEndOfDuration,
    hasSentRefCode,
    hasPendingCommandUpdateStudy,
    isStopping,
    isHideMarkAddressedStudy,
    referringPhysicianOptions,
    interprettingPhysicianOptions,
    onCloseDrawer,
    showStudyInfoDrawer,
    toggleDisplaySolutionsDrawer,
    resendReferenceCode,
    toggleDisplayDrawer,
    displayConfirmActivityModal,
    displayStopLiveEcgModal,
    displayViewLiveEcg,
    onClickConfirmActivityModal,
    handleStopStreaming,
    handleResumeStreaming,
    handleAbortClick,
    handleCancelClick,
    handleRightButtonClick,
    handleAssignDeviceClick,
    handleAssignDeviceDrawerClick,
    updateStudyData,
  };
};
